// web/profile.ts — the persistent cross-colony PLAYER PROFILE (meta-progression).
//
// The deterministic save (web/save.ts) holds ONE colony. This profile is the layer
// ABOVE it: lifetime statistics, a roll of honour of past colonies, and the
// evolution archive (genes/morphs/box tiers/mutation power discovered across every
// colony you've ever run. It is PURE PRESENTATION STATE — never part of the sim,
// the action log, determinism, or any hash — so it cannot affect replay. It lives
// in its own localStorage key and accumulates by simple, monotonic folds.
//
// The menu reads a SNAPSHOT of the live colony that we fold in at save time, so the
// CONTINUE card never has to replay the simulation to show rank/age/population.

const PROFILE_KEY = 'brood.profile.v1';
export const PROFILE_VERSION = 1;

const ARCHIVE_CAP = 12;   // past colonies kept in the roll of honour (newest first)
const MORPH_CAP = 48;     // distinct morph/discovery names remembered

/** A point-in-time portrait of one colony (folded from the live session). */
export interface ColonySnapshot {
  name: string;
  seed: number;
  createdAt: number;  // epoch ms when the colony was founded
  age: number;        // colony age in ticks
  generations: number;
  rank: number;       // 1-based leaderboard rank; 0 = unknown
  ofColonies: number;
  population: number;
  identity: string;   // emergent specialization name ("Industrial", "Forming"…)
  score: number;      // evolution score (leaderboard peak)
  apexKills: number;
  regionsHeld: number;
  genes: number;      // genes in the inventory
}

/** A retired colony in the archive (a snapshot + how/when it ended). */
export interface ColonyArchive extends ColonySnapshot {
  retiredAt: number;
  cause: string;      // 'replaced' | 'extinct'
}

/** The whole persistent profile. */
export interface PlayerProfile {
  version: number;
  coloniesCreated: number;
  boxesOpened: number;
  genesSacrificed: number;
  // Cumulative totals carried from RETIRED colonies; the live colony's values are
  // added on top at display time (see lifetimeGenerations / lifetimeApexes).
  retiredGenerations: number;
  retiredApexes: number;
  peakScore: number;
  peakEnergy: number;   // "total mutation power" — best Mutation-Energy pool ever held
  bestRank: number;     // lowest (best) rank number ever; 0 = none yet
  mostRegions: number;
  geneTypesSeen: number[];  // distinct gene TYPE ids ever looted
  morphsSeen: string[];     // distinct discovered morph / notable-discovery names
  tiersOpened: number[];    // box tier indices ever opened
  archives: ColonyArchive[];
  current?: ColonySnapshot | undefined; // the in-progress colony (folded at save)
}

export function defaultProfile(): PlayerProfile {
  return {
    version: PROFILE_VERSION,
    coloniesCreated: 0,
    boxesOpened: 0,
    genesSacrificed: 0,
    retiredGenerations: 0,
    retiredApexes: 0,
    peakScore: 0,
    peakEnergy: 0,
    bestRank: 0,
    mostRegions: 0,
    geneTypesSeen: [],
    morphsSeen: [],
    tiersOpened: [],
    archives: [],
  };
}

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    const k = '__brood_profile_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    return null;
  }
}

export function loadProfile(): PlayerProfile {
  const s = storage();
  if (!s) return defaultProfile();
  const raw = s.getItem(PROFILE_KEY);
  if (raw === null) return defaultProfile();
  try {
    const parsed = JSON.parse(raw) as Partial<PlayerProfile>;
    if (typeof parsed !== 'object' || parsed === null) return defaultProfile();
    // Merge over defaults so a profile written by an older build stays valid.
    return { ...defaultProfile(), ...parsed };
  } catch {
    return defaultProfile();
  }
}

export function writeProfile(p: PlayerProfile): void {
  const s = storage();
  if (!s) return;
  try { s.setItem(PROFILE_KEY, JSON.stringify(p)); } catch { /* quota / disabled — ignore */ }
}

// --- Lifetime accessors (retired totals + the live colony on top) ------------

export function lifetimeGenerations(p: PlayerProfile): number {
  return p.retiredGenerations + (p.current ? p.current.generations : 0);
}
export function lifetimeApexes(p: PlayerProfile): number {
  return p.retiredApexes + (p.current ? p.current.apexKills : 0);
}

// --- Folds (each one writes through) -----------------------------------------

function union(into: number[], add: readonly number[]): void {
  for (const v of add) if (!into.includes(v)) into.push(v);
}

/**
 * Refresh the live-colony snapshot + roll up the "best ever" aggregates. Called at
 * each save, so the menu always reflects the latest without replaying.
 */
export function foldCurrent(
  p: PlayerProfile,
  snap: ColonySnapshot,
  geneTypes: readonly number[],
  morphs: readonly string[],
  energy: number,
): void {
  p.current = snap;
  if (snap.score > p.peakScore) p.peakScore = snap.score;
  if (energy > p.peakEnergy) p.peakEnergy = energy;
  if (snap.rank > 0 && (p.bestRank === 0 || snap.rank < p.bestRank)) p.bestRank = snap.rank;
  if (snap.regionsHeld > p.mostRegions) p.mostRegions = snap.regionsHeld;
  union(p.geneTypesSeen, geneTypes);
  for (const m of morphs) {
    if (!p.morphsSeen.includes(m)) p.morphsSeen.push(m);
  }
  if (p.morphsSeen.length > MORPH_CAP) p.morphsSeen = p.morphsSeen.slice(-MORPH_CAP);
  writeProfile(p);
}

/** Count a freshly-opened Evolution Box (+ any duplicates it sacrificed + its tier). */
export function recordBoxOpened(p: PlayerProfile, sacrificed: number, tier: number, energyAfter: number): void {
  p.boxesOpened += 1;
  if (sacrificed > 0) p.genesSacrificed += sacrificed;
  if (tier >= 0 && !p.tiersOpened.includes(tier)) p.tiersOpened.push(tier);
  if (energyAfter > p.peakEnergy) p.peakEnergy = energyAfter;
  writeProfile(p);
}

/** Count genes consumed by a Fusion (the explicit duplicate sink). */
export function recordFusion(p: PlayerProfile, consumed: number): void {
  if (consumed > 0) p.genesSacrificed += consumed;
  writeProfile(p);
}

/** Count gene(s) sacrificed for Mutation Energy (manual or auto loot resolution). */
export function recordSacrifice(p: PlayerProfile, count: number): void {
  if (count > 0) p.genesSacrificed += count;
  writeProfile(p);
}

/** Begin a brand-new colony: archive the old one (if any) and bump the counter. */
export function startColony(p: PlayerProfile, cause = 'replaced'): void {
  archiveCurrent(p, cause);
  p.coloniesCreated += 1;
  writeProfile(p);
}

/** Move the live colony into the archive, accumulating its lifetime contribution. */
export function archiveCurrent(p: PlayerProfile, cause: string): void {
  const cur = p.current;
  if (!cur) return;
  p.archives.unshift({ ...cur, retiredAt: Date.now(), cause });
  if (p.archives.length > ARCHIVE_CAP) p.archives.length = ARCHIVE_CAP;
  p.retiredGenerations += cur.generations;
  p.retiredApexes += cur.apexKills;
  p.current = undefined;
  writeProfile(p);
}

/** Drop the live colony without archiving (used when the save is deleted). */
export function dropCurrent(p: PlayerProfile): void {
  p.current = undefined;
  writeProfile(p);
}
